import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { getGelanggangDB, saveGelanggangDB, getMembersDB, getNoticesDB } from '../../services/db';
import { GelanggangDetail, Gelanggang, UserProfile, NoticeItem } from '../../types';
import { PageHeader } from '../../components/common/PageHeader';
import { getGoogleMapsUrl, getGelanggangDisplayAddress, formatPublicName, isSuperAdmin, isUserAbove18, canEditGelanggangSessionAndPrice, isFamilyMember, getUserRoles, hasUserRole, hasRolePermission } from '../../utils/formatters';
import { isEligibleForMonthlyFee, parseGelanggangClasses, resolveMemberEnrollments } from '../../utils/pricing';
import { GelanggangManagementModal } from '../../components/common/GelanggangManagementModal';
import { GelanggangCalendarView } from '../../components/common/GelanggangCalendarView';
import { GelanggangSessionManageView } from '../../components/common/GelanggangSessionManageView';
import { BengkungCulaBadge } from '../../components/common/BengkungCulaBadge';
import { saveViewScroll, restoreViewScroll, resetScrollToTop } from '../../utils/scrollRestoration';
import { 
  MapPin, 
  Navigation, 
  Edit2, 
  Plus, 
  Building, 
  BookOpen, 
  CheckCircle2, 
  DollarSign, 
  Target, 
  Sparkles,
  ShieldAlert,
  Loader2,
  Trash2,
  HandFist,
  Users,
  ChevronDown,
  ChevronUp,
  Settings,
  X,
  Calendar,
  Clock
} from 'lucide-react';

export const GelanggangPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [gelanggangDetails, setGelanggangDetails] = useState<GelanggangDetail[]>([]);
  const [membersList, setMembersList] = useState<UserProfile[]>([]);
  const [noticesList, setNoticesList] = useState<NoticeItem[]>([]);
  const [activeSubTab, setActiveSubTab] = useState<'kelas' | 'tetapan'>('kelas');
  const [selectedGelanggangFilter, setSelectedGelanggangFilter] = useState<string>('Semua');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isGelanggangModalOpen, setIsGelanggangModalOpen] = useState<boolean>(false);
  const [selectedGelanggangToEdit, setSelectedGelanggangToEdit] = useState<GelanggangDetail | null>(null);
  const [isAddGelanggangMode, setIsAddGelanggangMode] = useState<boolean>(false);

  // Dedicated calendar pageview state
  const [selectedGelanggangForCalendar, setSelectedGelanggangForCalendar] = useState<GelanggangDetail | null>(null);
  // Dedicated session & pricing management pageview state
  const [selectedGelanggangForSession, setSelectedGelanggangForSession] = useState<GelanggangDetail | null>(null);

  // Collapsible enrolled members list state
  const [expandedEnrolledList, setExpandedEnrolledList] = useState<Record<string, boolean>>({});

  const isAhliBiasa = !currentUser || (getUserRoles(currentUser).every(r => r === 'Ahli' || r === 'Waris') && !isSuperAdmin(currentUser));
  const isPengerusi = Boolean(currentUser && (isSuperAdmin(currentUser) || hasUserRole(currentUser, 'Pengerusi')));
  const isKetuaGelanggang = Boolean(currentUser && (isSuperAdmin(currentUser) || hasUserRole(currentUser, 'Ketua Gelanggang')));
  const canManageGelanggang = Boolean(currentUser && hasRolePermission(currentUser, ['Pengerusi', 'Ketua Gelanggang', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari']));
  const canViewFinancialRevenue = Boolean(currentUser && hasRolePermission(currentUser, ['Pengerusi', 'Ketua Gelanggang', 'Bendahari', 'Timbalan Pengerusi', 'Bendahari Gelanggang', 'Superadmin']));

  const refreshGelanggangData = () => {
    setIsLoading(true);
    Promise.all([getGelanggangDB(), getMembersDB(), getNoticesDB()]).then(([gData, mData, nData]) => {
      setGelanggangDetails(gData);
      setMembersList(mData);
      setNoticesList(nData);
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    refreshGelanggangData();
  }, []);

  // Lock body scroll when any modal is opened
  useEffect(() => {
    if (isGelanggangModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isGelanggangModalOpen]);

  // Listen to URL parameters for direct deep-linking or browser back/forward navigation
  useEffect(() => {
    const checkUrlParams = () => {
      const params = new URLSearchParams(window.location.search);
      const view = params.get('view');
      const id = params.get('id');

      if (view === 'calendar' && id) {
        const found = gelanggangDetails.find(g => g.id === id);
        if (found) {
          setSelectedGelanggangForCalendar(found);
          setSelectedGelanggangForSession(null);
          return;
        }
      }
      if ((view === 'session' || view === 'schedule' || view === 'sesi') && id) {
        const found = gelanggangDetails.find(g => g.id === id);
        if (found) {
          setSelectedGelanggangForSession(found);
          setSelectedGelanggangForCalendar(null);
          return;
        }
      }
      setSelectedGelanggangForCalendar(null);
      setSelectedGelanggangForSession(null);
    };

    if (gelanggangDetails.length > 0) {
      checkUrlParams();
    }
    window.addEventListener('popstate', checkUrlParams);
    return () => window.removeEventListener('popstate', checkUrlParams);
  }, [gelanggangDetails]);

  const handleOpenCalendar = (g: GelanggangDetail) => {
    saveViewScroll('gelanggang_list');
    setSelectedGelanggangForCalendar(g);
    setSelectedGelanggangForSession(null);
    const url = new URL(window.location.href);
    url.searchParams.set('view', 'calendar');
    url.searchParams.set('id', g.id);
    window.history.pushState(null, '', url.toString());
    resetScrollToTop();
  };

  const handleBackFromCalendar = () => {
    setSelectedGelanggangForCalendar(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('view');
    url.searchParams.delete('id');
    const newUrl = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '');
    window.history.pushState(null, '', newUrl);
    restoreViewScroll('gelanggang_list');
  };

  const handleOpenSessionManage = (g: GelanggangDetail) => {
    saveViewScroll('gelanggang_list');
    setSelectedGelanggangForSession(g);
    setSelectedGelanggangForCalendar(null);
    const url = new URL(window.location.href);
    url.searchParams.set('view', 'session');
    url.searchParams.set('id', g.id);
    window.history.pushState(null, '', url.toString());
    resetScrollToTop();
  };

  const handleBackFromSessionManage = () => {
    setSelectedGelanggangForSession(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('view');
    url.searchParams.delete('id');
    const newUrl = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '');
    window.history.pushState(null, '', newUrl);
    restoreViewScroll('gelanggang_list');
  };

  const handleSessionManageSaved = (updatedGel: GelanggangDetail) => {
    setGelanggangDetails(prev => prev.map(item => item.id === updatedGel.id ? updatedGel : item));
    setSelectedGelanggangForSession(updatedGel);
    refreshGelanggangData();
  };

  const isGelanggangOwner = (g: GelanggangDetail) => {
    return canEditGelanggangSessionAndPrice(currentUser, g);
  };

  // Helper to find all enrolled members for a specific class in a gelanggang across all roles
  const getEnrolledMembersForClass = (
    g: GelanggangDetail,
    clsName: string,
    allMembers: UserProfile[]
  ): UserProfile[] => {
    const isMemanah = clsName.toLowerCase().includes('memanah');
    const isDewasa = clsName.toLowerCase().includes('dewasa');
    const isMuda = clsName.toLowerCase().includes('muda') || clsName.toLowerCase().includes('kanak');
    const isSilatGeneral = clsName.toLowerCase().includes('silat');
    const gClasses = parseGelanggangClasses(g.name, g.classes);
    const hasDewasaMudaSplit = gClasses.some(c => c.name.toLowerCase().includes('dewasa')) &&
                               gClasses.some(c => c.name.toLowerCase().includes('muda') || c.name.toLowerCase().includes('kanak'));

    return allMembers.filter(member => {
      // Exclude non-active / rejected status if specified
      if (member.status && !['Aktif', 'Menunggu Pengesahan'].includes(member.status)) return false;

      // Exclude Waris (guardians/parents) from registered pesilat list
      if (member.role === 'Waris') return false;

      // Exclude Ketua Gelanggang / court leader from registered pesilat list
      const isLeader = member.role === 'Ketua Gelanggang' || 
        (g.leaderName && member.name && (
          member.name.trim().toLowerCase() === g.leaderName.trim().toLowerCase() ||
          member.name.toLowerCase().includes(g.leaderName.toLowerCase()) ||
          g.leaderName.toLowerCase().includes(member.name.toLowerCase())
        ));
      if (isLeader) return false;

      // 1. Explicit JSON enrollments in sessionTime
      if (member.sessionTime) {
        try {
          const parsed = JSON.parse(member.sessionTime);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed.some(en => 
              (en.gelanggang === g.name || en.gelanggang === 'Semua Gelanggang') && 
              Array.isArray(en.plans) && en.plans.includes(clsName)
            );
          }
        } catch(e) {}
      }

      // 2. Explicit text sessionTime mentioning class name (e.g. "Memanah" or "Silat Dewasa")
      if (member.sessionTime && typeof member.sessionTime === 'string') {
        const sLower = member.sessionTime.toLowerCase();
        if (sLower.includes(clsName.toLowerCase())) {
          if (member.gelanggang === g.name || member.gelanggang === 'Semua Gelanggang') return true;
        }
      }

      // 3. Fallback matching for member registered at this gelanggang
      if (member.gelanggang === g.name) {
        // Archery / Memanah requires explicit enrollment
        if (isMemanah) return false;

        // If gelanggang differentiates between adult and youth
        if (hasDewasaMudaSplit) {
          const isYouth = member.relationship === 'Anak' || (member.icNo ? !isUserAbove18(member.icNo) : false);
          if (isYouth) {
            return isMuda;
          } else {
            return isDewasa;
          }
        }

        // Standard silat class: matches if this is a silat class or primary class
        if (isSilatGeneral) return true;
        if (gClasses.length > 0 && gClasses[0].name === clsName) return true;
      }

      return false;
    });
  };

  // Dynamic stats calculation based on filter for better readable data analysis
  const getFilteredStats = () => {
    let totalEnrolled = 0;
    let silatCount = 0;
    let memanahCount = 0;
    let totalRevenue = 0;

    const targetGels = gelanggangDetails
      .filter(g => g.status === 'Aktif')
      .filter(g => selectedGelanggangFilter === 'Semua' || g.name === selectedGelanggangFilter);

    const countedMemberIds = new Set<string>();

    targetGels.forEach(g => {
      const gClasses = parseGelanggangClasses(g.name, g.classes);
      gClasses.forEach(cls => {
        const enrolled = getEnrolledMembersForClass(g, cls.name, membersList);
        enrolled.forEach(mem => {
          countedMemberIds.add(mem.id);
          if (cls.name.toLowerCase().includes('memanah')) {
            memanahCount++;
          } else {
            silatCount++;
          }
          // Only fee-paying students contribute to estimated financial revenue
          if (isEligibleForMonthlyFee(mem)) {
            totalRevenue += cls.price;
          }
        });
      });
    });

    totalEnrolled = countedMemberIds.size;
    return { totalEnrolled, silatCount, memanahCount, totalRevenue };
  };

  const stats = getFilteredStats();

  // If a gelanggang is selected for session & price management, render dedicated pageview
  if (selectedGelanggangForSession) {
    return (
      <GelanggangSessionManageView
        gelanggang={selectedGelanggangForSession}
        onBack={handleBackFromSessionManage}
        onSaved={handleSessionManageSaved}
        onOpenCalendar={handleOpenCalendar}
        membersList={membersList}
        notices={noticesList}
      />
    );
  }

  // If a gelanggang is selected for calendar view, render dedicated pageview
  if (selectedGelanggangForCalendar) {
    return (
      <GelanggangCalendarView
        gelanggang={selectedGelanggangForCalendar}
        onBack={handleBackFromCalendar}
        notices={noticesList}
        membersList={membersList}
        currentUser={currentUser || undefined}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        badgeText="Pengurusan Latihan"
        badgeIcon={Building}
        title="Gelanggang & Sesi Kelas"
      >
        {activeSubTab === 'tetapan' && isPengerusi && (
          <button
            onClick={() => {
              setSelectedGelanggangToEdit(null);
              setIsAddGelanggangMode(true);
              setIsGelanggangModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-extrabold rounded-xl transition-all shadow-xs shrink-0 cursor-pointer border-none"
          >
            <Plus className="w-4 h-4" />
            <span>Tambah Gelanggang</span>
          </button>
        )}
      </PageHeader>

      {/* Tabs list switcher & Gelanggang Dropdown Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-3xl border border-slate-200 shadow-2xs">
        <div className="flex gap-2 p-1 bg-slate-100 rounded-2xl border border-slate-200/80 w-fit">
          <button
            onClick={() => setActiveSubTab('kelas')}
            className={`py-2 px-4 rounded-xl text-xs font-extrabold transition-all flex items-center gap-1.5 cursor-pointer border-none ${
              activeSubTab === 'kelas'
                ? 'bg-white text-asmm-red shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            <span>Senarai Kelas & Aktiviti</span>
          </button>

          {canManageGelanggang && (
            <button
              onClick={() => setActiveSubTab('tetapan')}
              className={`py-2 px-4 rounded-xl text-xs font-extrabold transition-all flex items-center gap-1.5 cursor-pointer border-none ${
                activeSubTab === 'tetapan'
                  ? 'bg-white text-asmm-red shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Edit2 className="w-4 h-4" />
              <span>Tetapan Gelanggang</span>
            </button>
          )}
        </div>

        {/* Dropdown on the right (only visible when in 'kelas' tab) */}
        {activeSubTab === 'kelas' && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">Pilih Gelanggang:</span>
            <select
              value={selectedGelanggangFilter}
              onChange={(e) => setSelectedGelanggangFilter(e.target.value)}
              className="px-3.5 py-2.5 bg-slate-50 border border-slate-300 hover:border-slate-400 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-asmm-red/20 focus:border-asmm-red transition-all cursor-pointer"
            >
              <option value="Semua">Semua Gelanggang</option>
              {gelanggangDetails.filter(g => g.status === 'Aktif').map((g) => (
                <option key={g.id} value={g.name}>{g.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* STATS ANALYTICS OVERVIEW CARD GRID */}
      {activeSubTab === 'kelas' && !isLoading && (
        <div className={`grid grid-cols-2 ${canViewFinancialRevenue ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4 animate-fade-in`}>
          {/* Total Enrolled */}
          <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200 shadow-2xs flex items-center gap-4">
            <span className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
              <Users className="w-5 h-5" />
            </span>
            <div className="space-y-0.5">
              <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider block">Jumlah Pesilat</span>
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 leading-none">{stats.totalEnrolled}</h3>
              <span className="text-[9px] text-slate-500 font-medium block">Aktif Latihan</span>
            </div>
          </div>

          {/* Silat Enrolled */}
          <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200 shadow-2xs flex items-center gap-4">
            <span className="p-3 bg-red-50 text-asmm-red rounded-2xl">
              <HandFist className="w-5 h-5" />
            </span>
            <div className="space-y-0.5">
              <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider block">Pesilat Silat</span>
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 leading-none">{stats.silatCount}</h3>
              <span className="text-[9px] text-slate-500 font-medium block">Kelas Silat</span>
            </div>
          </div>

          {/* Memanah Enrolled */}
          <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200 shadow-2xs flex items-center gap-4">
            <span className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
              <Target className="w-5 h-5" />
            </span>
            <div className="space-y-0.5">
              <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider block">Pesilat Memanah</span>
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 leading-none">{stats.memanahCount}</h3>
              <span className="text-[9px] text-slate-500 font-medium block">Kelas Memanah</span>
            </div>
          </div>

          {/* Estimated Monthly Revenue - HANYA UNTUK PENGURUSAN / KETUA GELANGGANG */}
          {canViewFinancialRevenue && (
            <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200 shadow-2xs flex items-center gap-4">
              <span className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl">
                <DollarSign className="w-5 h-5" />
              </span>
              <div className="space-y-0.5">
                <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider block">Hasil Yuran Bulanan</span>
                <h3 className="text-xl sm:text-2xl font-black text-emerald-700 leading-none">RM {stats.totalRevenue}</h3>
                <span className="text-[9px] text-slate-500 font-medium block">Anggaran Kasar</span>
              </div>
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="bg-white p-12 rounded-3xl border border-slate-200 text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-asmm-red mx-auto" />
          <p className="text-xs text-slate-500 font-extrabold">Memuatkan butiran gelanggang...</p>
        </div>
      ) : (
        <>
          {/* TAB 1: SENARAI KELAS & AKTIVITI */}
          {activeSubTab === 'kelas' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {gelanggangDetails
                .filter(g => g.status === 'Aktif')
                .filter(g => selectedGelanggangFilter === 'Semua' || g.name === selectedGelanggangFilter)
                .map((g) => {
                const mapsUrl = getGoogleMapsUrl(g);
                const classes = parseGelanggangClasses(g.name, g.classes);

                return (
                  <div key={g.id} className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm flex flex-col justify-between hover:border-slate-300 transition-all">
                    <div className="p-6 space-y-4">
                      {/* Gelanggang Info Header */}
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="space-y-1">
                          <button
                            type="button"
                            onClick={() => handleOpenCalendar(g)}
                            className="text-left group inline-flex items-center gap-2 cursor-pointer bg-transparent border-none p-0 focus:outline-none"
                            title="Klik untuk lihat kalendar bulanan gelanggang"
                          >
                            <h4 className="text-base font-black text-slate-900 group-hover:text-asmm-red transition-colors flex items-center gap-1.5">
                              <span>{g.name}</span>
                              <Calendar className="w-4 h-4 text-asmm-red opacity-80 group-hover:scale-110 group-hover:opacity-100 transition-all shrink-0" />
                            </h4>
                          </button>
                          <div>
                            <a
                              href={mapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-asmm-red font-semibold leading-relaxed"
                            >
                              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              <span className="underline">{getGelanggangDisplayAddress(g)}</span>
                            </a>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleOpenCalendar(g)}
                            className="inline-flex items-center gap-1 text-[11px] font-extrabold text-asmm-red hover:text-white bg-red-50 hover:bg-asmm-red border border-red-200/80 px-2.5 py-1 rounded-xl transition-all cursor-pointer shadow-3xs"
                            title="Buka Kalendar Bulanan Sesi"
                          >
                            <Calendar className="w-3.5 h-3.5" />
                            <span>Kalendar Sesi</span>
                          </button>
                          <span className="text-[10px] font-extrabold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-200">
                            Aktif
                          </span>
                        </div>
                      </div>

                      {/* Leader & Schedule Details */}
                      <div className="text-xs text-slate-600 space-y-1.5 bg-slate-50 p-3.5 rounded-2xl border border-slate-100">
                        <div>Ketua Gelanggang: <strong className="text-asmm-red font-bold">{formatPublicName(g.leaderName)}</strong></div>
                        <div>Jadual Latihan: <strong className="text-slate-800 font-bold">{g.sessionTime}</strong></div>
                      </div>

                      {/* Offered classes section */}
                      <div className="space-y-3 pt-2">
                        <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-1">
                          <div className="flex items-center gap-2">
                            <span className="block text-[11px] font-extrabold text-slate-400 uppercase tracking-wider">Aktiviti & Kelas Ditawarkan</span>
                            <span className="text-[10px] font-black text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
                              {membersList.filter(m => {
                                if (m.status && !['Aktif', 'Menunggu Pengesahan'].includes(m.status)) return false;
                                if (m.gelanggang !== g.name) return false;
                                if (m.role === 'Waris') return false;
                                if (m.role === 'Ketua Gelanggang') return false;
                                if (g.leaderName && m.name && (
                                  m.name.trim().toLowerCase() === g.leaderName.trim().toLowerCase() ||
                                  m.name.toLowerCase().includes(g.leaderName.toLowerCase()) ||
                                  g.leaderName.toLowerCase().includes(m.name.toLowerCase())
                                )) return false;
                                return true;
                              }).length} Pesilat Berdaftar
                            </span>
                          </div>
                          {isGelanggangOwner(g) && (
                            <button
                              onClick={() => handleOpenSessionManage(g)}
                              className="inline-flex items-center gap-1 text-[10px] font-black text-asmm-red hover:text-asmm-crimson transition-colors border-none bg-transparent cursor-pointer"
                            >
                              <Settings className="w-3.5 h-3.5" />
                              <span>Urus Sesi & Harga</span>
                            </button>
                          )}
                        </div>
                        <div className="space-y-3">
                          {classes.map((cls, idx) => {
                            const isMemanah = cls.name.toLowerCase().includes('memanah');
                            
                            // Find enrolled members across all roles for this gelanggang and class
                            const enrolled = getEnrolledMembersForClass(g, cls.name, membersList);

                            const listKey = `${g.id}-${cls.name}`;
                            const isExpanded = !!expandedEnrolledList[listKey];

                            return (
                              <div key={idx} className="flex flex-col gap-2 bg-white p-3.5 rounded-2xl border border-slate-200 shadow-2xs">
                                <div className="flex items-start gap-3">
                                  <span className={`p-2 rounded-xl shrink-0 ${isMemanah ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-asmm-red'}`}>
                                    {isMemanah ? <Target className="w-4 h-4" /> : <HandFist className="w-4 h-4" />}
                                  </span>
                                  <div className="space-y-0.5 min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-xs font-extrabold text-slate-900">Kelas {cls.name}</span>
                                      <span className="text-xs font-black text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-lg border border-emerald-200">
                                        RM{cls.price} / bln
                                      </span>
                                    </div>
                                    {cls.schedule ? (
                                      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-700 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-200">
                                        <Clock className="w-3 h-3 text-asmm-red shrink-0" />
                                        <span>Waktu Sesi: <strong className="text-slate-900 font-mono">{cls.schedule}</strong></span>
                                      </div>
                                    ) : null}
                                    <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
                                      {isMemanah 
                                        ? 'Latihan kemahiran memanah tradisional (Archery) merangkumi teknik panahan statik dan dinamik sunnah.' 
                                        : 'Silibus seni mempertahankan diri Seni Silat Awang Sulung Merah Muda Kedah lengkap dengan aspek fizikal dan rohani.'}
                                    </p>
                                  </div>
                                </div>

                                {/* Enrolled members count and collapsible breakdown trigger */}
                                <div className="pl-11 border-t border-slate-100 pt-2 flex flex-col gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedEnrolledList({ ...expandedEnrolledList, [listKey]: !isExpanded })}
                                    className="flex items-center gap-1.5 text-[10px] font-black text-slate-600 hover:text-slate-900 transition-colors border-none bg-transparent cursor-pointer w-fit p-0 group"
                                  >
                                    <Users className="w-3.5 h-3.5 text-slate-400 group-hover:text-asmm-red transition-colors" />
                                    <span className="font-extrabold">{enrolled.length} Pesilat Terdaftar</span>
                                    {isExpanded ? <ChevronUp className="w-3 h-3 text-slate-400" /> : <ChevronDown className="w-3 h-3 text-slate-400" />}
                                  </button>

                                  {isExpanded && (
                                    <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100 space-y-1.5 mt-1 animate-fade-in">
                                      {enrolled.length > 0 ? (
                                        enrolled.map(mem => {
                                          const isMe = mem.id === currentUser?.id;
                                          const isMyFamily = !isMe && isFamilyMember(mem, currentUser);

                                          return (
                                            <div 
                                              key={mem.id} 
                                              className={`flex items-center justify-between gap-2 text-[10px] p-1.5 rounded-lg transition-colors ${
                                                isMe 
                                                  ? 'bg-emerald-50/80 border border-emerald-200' 
                                                  : isMyFamily 
                                                  ? 'bg-purple-50/60 border border-purple-200' 
                                                  : 'bg-white/80 hover:bg-white border border-slate-100'
                                              }`}
                                            >
                                              <div className="flex items-center gap-1.5 min-w-0">
                                                <span className="font-extrabold text-slate-800 truncate">
                                                  {formatPublicName(mem.name)}
                                                </span>
                                                {isMe && (
                                                  <span className="bg-emerald-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full shrink-0">
                                                    Anda
                                                  </span>
                                                )}
                                                {isMyFamily && (
                                                  <span className="bg-purple-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full shrink-0">
                                                    Keluarga
                                                  </span>
                                                )}
                                              </div>
                                              <div className="flex items-center gap-2 shrink-0">
                                                <span className="font-mono text-slate-400 text-[9px]">{mem.id}</span>
                                                <BengkungCulaBadge 
                                                  bengkung={mem.bengkung || 'Putih 1'} 
                                                  size="sm" 
                                                  fullWidth={false} 
                                                  className="!h-6 !text-[10px] !pl-2.5 !pr-7 rounded-lg shrink-0 shadow-3xs"
                                                  minWidth="min-w-[95px]"
                                                />
                                              </div>
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <div className="text-[10px] text-slate-400 font-bold italic py-1">
                                          Tiada pesilat yang berdaftar untuk kelas ini.
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Footer Nav */}
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-extrabold text-blue-700 hover:text-blue-800 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-xl transition-all"
                      >
                        <Navigation className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                        <span>Pandu Arah Google Maps</span>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* TAB 2: TETAPAN GELANGGANG */}
          {activeSubTab === 'tetapan' && canManageGelanggang && (
            <div className="space-y-6">
              <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 space-y-5 shadow-2xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900">
                      Senarai Urus Gelanggang Latihan
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Uruskan lokasi latihan, pautan GPS, nama Ketua Gelanggang, jenis kelas, dan harga yuran di bawah seliaan anda.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {gelanggangDetails.map((g) => {
                    const canEditSession = canEditGelanggangSessionAndPrice(currentUser, g);
                    const canEditSettings = canEditSession || isPengerusi;
                    const mapsUrl = getGoogleMapsUrl(g);
                    const classes = parseGelanggangClasses(g.name, g.classes);

                    return (
                      <div
                        key={g.id}
                        className="p-5 bg-slate-50/70 border border-slate-200 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-slate-300 transition-all"
                      >
                        <div className="space-y-2.5 min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="p-1.5 bg-red-50 text-asmm-red rounded-lg">
                              <MapPin className="w-4 h-4 shrink-0" />
                            </span>
                            <button
                              type="button"
                              onClick={() => handleOpenCalendar(g)}
                              className="text-left group inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-none p-0 focus:outline-none"
                              title="Klik untuk lihat kalendar bulanan gelanggang"
                            >
                              <h4 className="font-black text-sm text-slate-900 group-hover:text-asmm-red transition-colors flex items-center gap-1.5">
                                <span>{g.name}</span>
                                <Calendar className="w-3.5 h-3.5 text-asmm-red opacity-80 group-hover:scale-110 group-hover:opacity-100 transition-all shrink-0" />
                              </h4>
                            </button>
                            <span className="text-[9px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full">
                              {g.status}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleOpenCalendar(g)}
                              className="inline-flex items-center gap-1 text-[10px] font-extrabold text-asmm-red bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-0.5 rounded-lg transition-all cursor-pointer ml-1"
                            >
                              <Calendar className="w-3 h-3" />
                              <span>Kalendar</span>
                            </button>
                          </div>

                          <div className="pl-9 space-y-1.5">
                            <span className="block text-xs text-slate-700 leading-relaxed font-semibold">
                              {getGelanggangDisplayAddress(g)}
                            </span>

                            <div className="text-xs text-slate-600 font-medium flex flex-wrap gap-x-5 gap-y-1 pt-1">
                              <div>Sesi Latihan: <strong className="text-slate-900 font-bold">{g.sessionTime}</strong></div>
                              <div>Ketua: <strong className="text-asmm-red font-bold">{formatPublicName(g.leaderName)}</strong></div>
                              <div>
                                Kelas Ditawarkan: <strong className="text-emerald-700">{classes.map(c => `${c.name} (RM${c.price})`).join(', ')}</strong>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Direct Edit Button for Authorized Leaders */}
                        <div className="pl-9 sm:pl-0 shrink-0 self-start sm:self-center">
                          {canEditSession || canEditSettings ? (
                            <div className="flex items-center gap-2">
                              {canEditSession && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenSessionManage(g)}
                                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer border-none"
                                >
                                  <Settings className="w-3.5 h-3.5 text-white" />
                                  <span>Urus Sesi & Harga</span>
                                </button>
                              )}
                              {canEditSettings && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedGelanggangToEdit(g);
                                    setIsAddGelanggangMode(false);
                                    setIsGelanggangModalOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer border-none"
                                >
                                  <Edit2 className="w-3.5 h-3.5 text-white" />
                                  <span>Tetapan</span>
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-400 font-bold bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-xl">
                              Gelanggang Lain
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Gelanggang Management Modal */}
      <GelanggangManagementModal
        isOpen={isGelanggangModalOpen}
        onClose={() => {
          setIsGelanggangModalOpen(false);
          setSelectedGelanggangToEdit(null);
          setIsAddGelanggangMode(false);
        }}
        onSaved={refreshGelanggangData}
        initialGelanggangToEdit={selectedGelanggangToEdit}
        isAddMode={isAddGelanggangMode}
      />
    </div>
  );
};
